import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';
import { resendService } from '../services/resendService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  margin-top: -80px;
  padding-top: 80px;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.lg};
  color: ${theme.colors.neutral.grey};
  text-align: center;
  margin-bottom: 40px;
`;

const QuickStartSection = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 20px;
`;

const PopularGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const PopularCard = styled.button`
  background: white;
  border: 2px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.lg};
  padding: 20px;
  text-align: left;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    border-color: #FFD700;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const PopularCardTitle = styled.h3`
  font-size: ${theme.typography.sizes.md};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const PopularCardDescription = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.grey};
  line-height: 1.5;
`;

const CustomCompareSection = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 24px;
  }
`;

const CompareForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  align-items: end;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.text.primary};
  background-color: white;
  cursor: pointer;
  transition: border-color ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: #FFD700;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${theme.colors.neutral.lightGrey};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.text.primary};
  transition: border-color ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: #FFD700;
  }
  
  &::placeholder {
    color: ${theme.colors.neutral.grey};
  }
`;

const CompareButton = styled.button`
  background-color: #FFD700;
  color: #232B3B;
  font-weight: ${theme.typography.fontWeights.bold};
  padding: 14px 20px;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.lg};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  margin-top: 20px;
  
  &:hover {
    background-color: #e6c200;
  }
  
  &:disabled {
    background-color: ${theme.colors.neutral.lightGrey};
    color: ${theme.colors.neutral.grey};
    cursor: not-allowed;
  }
`;

const RedFlagSection = styled.div`
  background-color: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: ${theme.borderRadius.lg};
  padding: 30px;
  margin-top: 40px;
`;

const RedFlagTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: #856404;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RedFlagList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 24px;
`;

const RedFlagItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  color: #856404;
  font-size: ${theme.typography.sizes.md};
  
  &::before {
    content: '❌';
    margin-right: 12px;
    flex-shrink: 0;
  }
`;

const RedFlagCTA = styled.p`
  font-weight: ${theme.typography.fontWeights.semibold};
  color: #856404;
  margin-top: 20px;
`;

const Compare: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([
    { company: '', productName: '' },
    { company: '', productName: '' },
    { company: '', productName: '' }
  ]);
  const [email, setEmail] = useState('');

  const companies = [
    'Select company...',
    'Allianz',
    'Athene',
    'AIG',
    'Brighthouse',
    'Equitable',
    'F&G',
    'Jackson National',
    'Lincoln Financial',
    'Nationwide',
    'Pacific Life',
    'Prudential',
    'Other'
  ];

  const handlePopularComparison = (comparison: string) => {
    // Navigate to Thank You page (popular comparisons don't need email)
    navigate('/thank-you', { 
      state: { 
        fromForm: true,
        comparisonType: comparison,
        email: 'not-provided' // Popular comparisons go straight through
      } 
    });
  };

  const handleCustomCompare = async (e: React.FormEvent) => {
    e.preventDefault();
    const validProducts = products.filter(p => p.company && p.productName);
    if (validProducts.length >= 2 && email) {
      try {
        // Send admin notification email
        await resendService.sendEmail({
          to: 'jake@averillagencies.com',
          subject: '📊 New Annuity Comparison Request',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">
              <div style="background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%); padding: 30px; text-align: center;">
                <h1 style="color: white; margin: 0; font-size: 28px;">Annuity Comparison Request</h1>
                <p style="color: #ffd700; margin: 10px 0 0 0; font-size: 16px;">Build Your Own Comparison Tool</p>
              </div>
              
              <div style="padding: 30px; background-color: #f9f9f9;">
                <div style="background-color: #ffd700; padding: 15px; border-radius: 8px; margin-bottom: 25px;">
                  <h2 style="color: #232B3B; margin: 0; font-size: 20px;">⚡ New Comparison Request Submitted</h2>
                </div>
                
                <div style="background-color: white; padding: 25px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                  <h3 style="color: #232B3B; margin-top: 0; border-bottom: 2px solid #ffd700; padding-bottom: 10px;">Customer Information</h3>
                  <p style="margin: 10px 0;"><strong>Email:</strong> ${email}</p>
                  <p style="margin: 10px 0;"><strong>Date & Time:</strong> ${new Date().toLocaleString()}</p>
                  <p style="margin: 10px 0;"><strong>Form Type:</strong> Custom Comparison Builder</p>
                </div>
                
                <div style="background-color: white; padding: 25px; border-radius: 8px; margin-top: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                  <h3 style="color: #232B3B; margin-top: 0; border-bottom: 2px solid #ffd700; padding-bottom: 10px;">Products to Compare</h3>
                  ${validProducts.map((product, index) => `
                    <div style="background-color: #f0f4f8; padding: 15px; border-radius: 6px; margin-bottom: 12px; border-left: 4px solid #ffd700;">
                      <h4 style="color: #232B3B; margin: 0 0 8px 0; font-size: 16px;">Product ${index + 1}</h4>
                      <p style="margin: 5px 0; color: #555;"><strong>Company:</strong> ${product.company}</p>
                      <p style="margin: 5px 0; color: #555;"><strong>Product Name:</strong> ${product.productName}</p>
                    </div>
                  `).join('')}
                </div>
                
                <div style="background-color: #232B3B; padding: 20px; border-radius: 8px; margin-top: 25px;">
                  <h3 style="color: #ffd700; margin: 0 0 15px 0; font-size: 18px;">📋 Recommended Actions</h3>
                  <ul style="color: white; margin: 0; padding-left: 20px; line-height: 1.8;">
                    <li>Run backtesting analysis on requested products</li>
                    <li>Prepare side-by-side comparison report</li>
                    <li>Follow up within 24 hours with results</li>
                    <li>Consider scheduling consultation to discuss findings</li>
                  </ul>
                </div>
                
                <div style="text-align: center; margin-top: 25px; padding: 20px; background-color: #e8f4f8; border-radius: 8px;">
                  <p style="color: #666; margin: 0; font-size: 14px;">
                    <strong>Page Source:</strong> /compare - Build Your Own Comparison Tool<br>
                    <span style="font-size: 12px; color: #999;">This user is actively comparing multiple annuity products</span>
                  </p>
                </div>
              </div>
            </div>
          `
        });

        // Send confirmation email to user
        await resendService.sendEmail({
          to: email,
          subject: 'Your Annuity Comparison Request Has Been Received',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%); padding: 30px; text-align: center;">
                <h1 style="color: white; margin: 0;">Comparison Request Received</h1>
              </div>
              <div style="padding: 30px;">
                <h2 style="color: #232B3B;">Thank You for Your Request!</h2>
                <p style="color: #666; font-size: 16px; line-height: 1.6;">
                  We've received your request to compare the following annuity products:
                </p>
                
                <div style="background-color: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
                  ${validProducts.map((product, index) => `
                    <div style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #ddd;">
                      <strong>Product ${index + 1}:</strong><br>
                      ${product.company} - ${product.productName}
                    </div>
                  `).join('')}
                </div>
                
                <h3 style="color: #232B3B;">What Happens Next?</h3>
                <p style="color: #666; line-height: 1.6;">
                  Our team will analyze these products using real historical data and prepare a comprehensive comparison for you. 
                  You can expect your results within 24 hours.
                </p>
                
                <p style="color: #666; line-height: 1.6;">
                  <strong>Need results faster?</strong><br>
                  Call us at 800-922-1894 and we can walk you through the comparison immediately.
                </p>
                
                <div style="text-align: center; margin-top: 30px;">
                  <a href="https://annuityanswers.com" style="background-color: #ffd700; color: #232B3B; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                    Visit Our Website
                  </a>
                </div>
              </div>
            </div>
          `
        });
      } catch (error) {
        console.error('Error sending comparison emails:', error);
        // Still navigate even if email fails
      }
      
      navigate('/thank-you', { 
        state: { 
          fromForm: true,
          products: validProducts,
          email: email
        } 
      });
    }
  };

  const updateProduct = (index: number, field: 'company' | 'productName', value: string) => {
    const newProducts = [...products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setProducts(newProducts);
  };

  const isFormValid = products.filter(p => 
    p.company && p.company !== 'Select company...' && p.productName
  ).length >= 2 && email;

  return (
    <SharedLayout>
      <PageWrapper>
        <ContentSection>
          <PageTitle>Compare Before You Buy</PageTitle>
          <PageSubtitle>
            See real historical performance, not hypothetical projections
          </PageSubtitle>

          {/* <QuickStartSection>
            <SectionTitle>Popular Comparisons</SectionTitle>
            <PopularGrid>
              <PopularCard onClick={() => handlePopularComparison('sp500-vs-managed')}>
                <PopularCardTitle>S&P 500 Index vs Managed Volatility</PopularCardTitle>
                <PopularCardDescription>
                  See how much managed volatility indices really cost you in returns
                </PopularCardDescription>
              </PopularCard>
              
              <PopularCard onClick={() => handlePopularComparison('top5-2025')}>
                <PopularCardTitle>Top 5 Fixed Index Annuities 2025</PopularCardTitle>
                <PopularCardDescription>
                  Compare the most popular FIAs based on actual historical performance
                </PopularCardDescription>
              </PopularCard>
              
              <PopularCard onClick={() => handlePopularComparison('structured-vs-fixed')}>
                <PopularCardTitle>Structured vs Fixed Index</PopularCardTitle>
                <PopularCardDescription>
                  Understand the real differences in risk and return profiles
                </PopularCardDescription>
              </PopularCard>
            </PopularGrid>
          </QuickStartSection> */}

          <CustomCompareSection>
            <SectionTitle>Build Your Own Comparison</SectionTitle>
            <p style={{ color: theme.colors.neutral.grey, marginBottom: '24px' }}>
              Add up to 3 products to compare side-by-side
            </p>
            
            <CompareForm onSubmit={handleCustomCompare}>
              {products.map((product, index) => (
                <ProductRow key={index}>
                  <FormGroup>
                    <Label>Product {index + 1} Company</Label>
                    <Select 
                      value={product.company}
                      onChange={(e) => updateProduct(index, 'company', e.target.value)}
                    >
                      {companies.map(company => (
                        <option key={company} value={company}>{company}</option>
                      ))}
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Product Name</Label>
                    <Input 
                      type="text"
                      placeholder="e.g., Power Index Elite Plus"
                      value={product.productName}
                      onChange={(e) => updateProduct(index, 'productName', e.target.value)}
                    />
                  </FormGroup>
                </ProductRow>
              ))}
              
              <FormGroup style={{ marginTop: '24px' }}>
                <Label>Your Email (for results)</Label>
                <Input 
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormGroup>
              
              <CompareButton type="submit" disabled={!isFormValid}>
                Compare These Products →
              </CompareButton>
            </CompareForm>
          </CustomCompareSection>

          <RedFlagSection>
            <RedFlagTitle>
              ⚠️ Red Flag Checklist
            </RedFlagTitle>
            <RedFlagList>
              <RedFlagItem>
                Uses "managed volatility" or "volatility controlled" indices
              </RedFlagItem>
              <RedFlagItem>
                Shows only hypothetical or back-tested returns
              </RedFlagItem>
              <RedFlagItem>
                Won't show actual historical performance data
              </RedFlagItem>
              <RedFlagItem>
                Promises over 7% returns with "no risk"
              </RedFlagItem>
              <RedFlagItem>
                Complex crediting methods with multiple moving parts
              </RedFlagItem>
              <RedFlagItem>
                Salespeople can't explain how the date lottery works
              </RedFlagItem>
            </RedFlagList>
            <RedFlagCTA>
              If any of these apply, you NEED our backtester before making a decision.
            </RedFlagCTA>
            <CompareButton 
              style={{ marginTop: '20px', width: 'auto', padding: '12px 24px' }}
              onClick={() => navigate('/ai-backtester')}
            >
              Start Backtesting Now →
            </CompareButton>
          </RedFlagSection>
        </ContentSection>
      </PageWrapper>
    </SharedLayout>
  );
};

export default Compare;